import { AppDispatcher } from 'dispatcher';
import remove from 'lodash/remove';
import slice from 'lodash/slice';
/* CONSTANTS */
import {
  type CallhomeCamera,
  type CallhomeCameraGroup,
  type CameraGroupId,
  type CHCameraId,
  type DeviceId,
  type ScheduleState,
  type SiteId,
} from '@solinkcorp/api-types';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { CallhomeApi, DeviceProxyApi, VmsApi } from 'clients';
import { type CallhomeCameraLink, type Point } from 'clients/CallhomeApiTypes'; // For an interface here
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import CameraStore from 'stores/CameraStore';
import * as CameraUtil from 'utils/CameraUtil';
import { type VideoDiscoveryCameraGroup, type VideoDiscoverySite } from 'utils/types';
import * as constants from 'vd-constants';
import {
  CAMERA_GROUP_TYPE_FLOORPLAN,
  CAMERA_GROUP_TYPE_STANDARD,
  LOCATION_REP_CAMERA_GROUPS_FAILED,
  MAP_CAMERA_GROUPS,
  OWNER_TYPE,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
  UPDATE_CAMERA_MOTION_ALARM_DATA,
  UPDATE_LOCATION_REP_CAMERA_GROUPS,
} from 'vd-constants';

const CameraActions = {
  addToRecentlyViewed({ id }: { id: CHCameraId }) {
    const impersonating = AuthService.isImpersonating;

    if (impersonating) {
      return;
    }

    const cameraIdGroups = CameraStore.getCameraIdGroups();
    const recentGroup = CameraUtil.getRecentCameraGroup(cameraIdGroups);
    const { cameraIds } = recentGroup;

    if (!cameraIds) {
      return;
    }

    let recentCameras = cameraIds.slice(); // clone

    remove(recentCameras, (cameraId) => id === cameraId);
    recentCameras.unshift(id);

    if (recentCameras.length > constants.RECENT_CAMERAS_GROUP_LIMIT) {
      recentCameras = slice(recentCameras, 0, constants.RECENT_CAMERAS_GROUP_LIMIT);
    }

    const data = { cameraIds: recentCameras };
    this.updateCameraGroup({ group: recentGroup, data });
  },

  updateCameraAnnotation(id: string, annotations: any[] | undefined) {
    // From PlayerOverlay.tsx
    AppDispatcher.dispatch(constants.UPDATE_CAMERA_ANNOTATION, {
      id,
      annotations,
    });
  },

  updateCameraLink(
    locationId: string,
    cameraLinkId: string,
    prop: string, // keyof CallhomeCameraLink
    value: boolean | Point,
  ) {
    AppDispatcher.dispatch(constants.UPDATE_CAMERA_LINK, {
      locationId,
      cameraLinkId,
      prop,
      value,
    });
  },

  insertCameraLink(locationId: string, newCameraLink: CallhomeCameraLink) {
    AppDispatcher.dispatch(constants.INSERT_CAMERA_LINK, {
      locationId,
      newCameraLink,
    });
  },

  deleteCameraLink(locationId: string, cameraLinkId: string) {
    AppDispatcher.dispatch(constants.DELETE_CAMERA_LINK, {
      locationId,
      cameraLinkId,
    });
  },

  getLocationRepCameraGroups() {
    CallhomeApi()
      .getAllCameraGroups({ type: CAMERA_GROUP_TYPE_FLOORPLAN })
      .then((response) => {
        AppDispatcher.dispatch(UPDATE_LOCATION_REP_CAMERA_GROUPS, {
          payload: response,
        });
      })
      .catch((err) => {
        console.error(err);
        AppDispatcher.dispatch(LOCATION_REP_CAMERA_GROUPS_FAILED, {});
      });
  },

  getCameraGroups() {
    CallhomeApi()
      .getAllCameraGroups({
        type: CAMERA_GROUP_TYPE_STANDARD,
        ownerType: {
          inq: [OWNER_TYPE.CUSTOMER, OWNER_TYPE.USER],
        },
      })
      .then((response) => {
        AppDispatcher.dispatch(constants.UPDATE_CAMERA_GROUPS, {
          payload: response,
        });
      })
      .catch((err) => {
        console.error(err);
      });
  },

  createCameraGroup({
    group,
    successCallback,
    errorCallback,
  }: {
    group: Array<string> | unknown;
    successCallback?: () => void;
    errorCallback?: () => void;
  }) {
    CallhomeApi()
      .createCameraGroup(group)
      .then((response) => {
        AppDispatcher.dispatch(constants.CREATE_CAMERA_GROUP, {
          payload: response,
        });
        successCallback?.();
        AnalyticsLogger.cameraGroupCreated({ group, status: STATUS_SUCCEEDED });
      })
      .catch((err) => {
        AnalyticsLogger.cameraGroupCreated({ group, status: STATUS_FAILED });
        console.error(err);
        errorCallback?.();
      });
  },

  updateCameraGroup({
    data,
    group,
    successCallback,
    errorCallback,
    analyticsMetadata = undefined,
  }: {
    data: Record<string, unknown>;
    group: CallhomeCameraGroup | VideoDiscoverySite | VideoDiscoveryCameraGroup;
    successCallback?: () => void;
    errorCallback?: () => void;
    analyticsMetadata?: any;
  }) {
    const { id } = group;

    if (id && data) {
      CallhomeApi()
        // TODO: Need to investigate if siteId here can cause problems.(overwriting with CameraGroupId)
        .updateCameraGroup(id as CameraGroupId, data)
        .then((response) => {
          AppDispatcher.dispatch(constants.UPDATE_CAMERA_GROUP, {
            payload: response,
          });
          successCallback?.();
          if (group.name !== constants.RECENT_CAMERAS_GROUP_NAME) {
            AnalyticsLogger.cameraGroupUpdated({
              group,
              status: STATUS_SUCCEEDED,
              analyticsMetadata,
            });
          }
        })
        .catch((err) => {
          AnalyticsLogger.cameraGroupUpdated({
            group,
            status: STATUS_FAILED,
            analyticsMetadata,
          });
          console.error(err);
          errorCallback?.();
        });
    } else {
      console.error('Missing id or payload when trying to update camera group');
      errorCallback?.();
    }
  },

  deleteCameraGroup({
    group,
    successCallback,
    errorCallback,
  }: {
    group: CallhomeCameraGroup;
    successCallback?: () => void;
    errorCallback?: () => void;
  }) {
    const { id } = group;
    CallhomeApi()
      .deleteCameraGroup(id)
      .then(() => {
        AppDispatcher.dispatch(constants.DELETE_CAMERA_GROUP, {
          payload: id,
        });
        successCallback?.();
        AnalyticsLogger.cameraGroupDeleted({ group, status: STATUS_SUCCEEDED });
      })
      .catch((err) => {
        AnalyticsLogger.cameraGroupDeleted({ group, status: STATUS_FAILED });
        console.error(err);
        errorCallback?.();
      });
  },

  mapCameraGroups: () => AppDispatcher.dispatch(MAP_CAMERA_GROUPS),

  async enableMotionAlarmsOnCamera({
    id,
    deviceUrl,
    deviceId,
    useProxy,
  }: {
    id: CHCameraId;
    deviceUrl: string | null;
    deviceId?: DeviceId;
    useProxy?: boolean;
  }) {
    const camera = await CallhomeApi().enableMotionAlarmsOnCamera(id);
    if (deviceUrl) {
      if (useProxy && deviceId) {
        await DeviceProxyApi().lightCheckin(deviceId);
      } else {
        await VmsApi(deviceUrl).lightCheckin();
      }
    }
    AppDispatcher.dispatch(UPDATE_CAMERA_MOTION_ALARM_DATA, { camera });
  },

  async removeCameraFromAlarm({
    camera,
    useProxy,
    isLocal,
    locationId,
    tags,
  }: {
    camera: CallhomeCamera;
    useProxy?: boolean;
    isLocal: boolean;
    locationId: SiteId;
    tags: string[];
  }) {
    const newCamera = await CallhomeApi().updateCamera(
      camera.id,
      {
        isMotionAlarmEnabled: false,
        cameraTagIds: tags,
      },
      '?filter={"include": ["motionAlarmSchedule"]}',
    );

    const accessibleAddress = getAccessibleAddress(camera, isLocal);

    if (accessibleAddress) {
      if (useProxy && camera.deviceId) {
        await DeviceProxyApi().lightCheckin(camera.deviceId);
      } else {
        await VmsApi(accessibleAddress).lightCheckin();
      }
    }

    AppDispatcher.dispatch(UPDATE_CAMERA_MOTION_ALARM_DATA, { camera: newCamera });
    AppDispatcher.dispatch(constants.CAMERA_TAGS_UPDATED_SUCCESS, {
      payload: { cameraId: camera.id, locationId, tags },
    });

    return newCamera;
  },

  async setCameraAlarmSchedule({ id, state }: { id: CHCameraId; state: ScheduleState }) {
    await CallhomeApi().setMotionAlarmScheduleForCamera({
      cameraId: id,
      state,
    });
    const camera = await CallhomeApi().enableMotionAlarmsOnCamera(id);
    AppDispatcher.dispatch(UPDATE_CAMERA_MOTION_ALARM_DATA, { camera });
  },

  async setCameraRegions({
    uniqueCameraId,
    regions,
    locationId,
  }: {
    uniqueCameraId: CHCameraId;
    regions: { type: string; gridRoi: string }[];
    locationId?: string;
  }) {
    try {
      const response = await CallhomeApi().updateCamera(uniqueCameraId, { regions });
      AppDispatcher.dispatch(constants.CAMERA_ROI_UPDATED_SUCCESS, {
        payload: { uniqueCameraId, regions, locationId },
      });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
};

export default CameraActions;
